import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram } from '../gpu/shader';
import { BaseEffect } from './base';
import { VERTEX_FULLSCREEN, FRAG_GLITCH } from '../shaders/glsl';
import { canReadCanvasPixels } from '../canvasSafe';

const DEFAULTS = {
  intensity: 0.5,
  rgbOffset: 4,
  noiseAmount: 0.15,
  scanLineOpacity: 0.25,
};

let glitchProg: WebGLShaderProgram | null = null;

/** Deterministic hash for CPU path (matches shader seed semantics). */
export function glitchHash(x: number, y: number, seed: number): number {
  const v = Math.sin(x * 127.1 + y * 311.7 + seed) * 43758.5453;
  return v - Math.floor(v);
}

export class GlitchEffect extends BaseEffect {
  seed: number;

  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'glitch', clip, DEFAULTS);
    this.seed = clip.seed ?? 42;
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!glitchProg) glitchProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_GLITCH);
    const seed = this.seed + context.frameIndex;
    return this.runShaderPass(frame, glitchProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_intensity', params.intensity);
      prog.setUniform1f('u_rgbOffset', params.rgbOffset);
      prog.setUniform1f('u_noiseAmount', params.noiseAmount);
      prog.setUniform1f('u_scanLineOpacity', params.scanLineOpacity);
      prog.setUniform1f('u_seed', seed);
      prog.setUniform1f('u_time', context.currentTime);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const seed = this.seed + context.frameIndex;
    const w = frame.width;
    const h = frame.height;
    const srcCtx = (frame.canvas as HTMLCanvasElement).getContext('2d');
    if (!srcCtx || !canReadCanvasPixels(frame.canvas as HTMLCanvasElement)) {
      ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
      const shift = params.rgbOffset * params.intensity * 0.5;
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = params.intensity * 0.35;
      ctx.drawImage(frame.canvas as CanvasImageSource, shift, 0, w, h);
      ctx.drawImage(frame.canvas as CanvasImageSource, -shift, 0, w, h);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      return { width: w, height: h, canvas, texture: null };
    }
    const src = srcCtx.getImageData(0, 0, w, h);
    const out = ctx.createImageData(w, h);
    const shift = Math.round(params.rgbOffset * params.intensity * 0.5);
    for (let y = 0; y < h; y += 1) {
      const block = Math.floor(y / (h / 24) + seed);
      const jitter = Math.round((glitchHash(block, seed, seed) - 0.5) * params.intensity * w * 0.04);
      for (let x = 0; x < w; x += 1) {
        const sx = Math.min(w - 1, Math.max(0, x + jitter));
        const ri = (y * w + Math.min(w - 1, sx + shift)) * 4;
        const gi = (y * w + sx) * 4;
        const bi = (y * w + Math.max(0, sx - shift)) * 4;
        const oi = (y * w + x) * 4;
        out.data[oi] = src.data[ri];
        out.data[oi + 1] = src.data[gi + 1];
        out.data[oi + 2] = src.data[bi + 2];
        out.data[oi + 3] = 255;
        if (y % 3 === 0) {
          out.data[oi] *= 1 - params.scanLineOpacity * params.intensity;
          out.data[oi + 1] *= 1 - params.scanLineOpacity * params.intensity;
          out.data[oi + 2] *= 1 - params.scanLineOpacity * params.intensity;
        }
        const n = glitchHash(x, y, seed) * params.noiseAmount * params.intensity * 255;
        out.data[oi] = Math.min(255, out.data[oi] + n - params.noiseAmount * params.intensity * 128);
        out.data[oi + 1] = Math.min(255, out.data[oi + 1] + n - params.noiseAmount * params.intensity * 128);
        out.data[oi + 2] = Math.min(255, out.data[oi + 2] + n - params.noiseAmount * params.intensity * 128);
      }
    }
    ctx.putImageData(out, 0, 0);
    return { width: w, height: h, canvas, texture: null };
  }
}
